import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { AsyncRequestService } from 'src/app/core/async-request/async-request.service';
import { SiteTypeEnum } from 'src/app/shared/enums';
import { API_URL_COMPANIES, API_URL_COMPANIES_V2, BasicService } from 'src/app/shared/services/basic.service';
import { ErrorHandlingService } from 'src/app/shared/services/error-handling.service';

import { AuthorizedData, AuthorizedDataParams } from './shared/models';

const ACCOUNT_EDIT_PATH = 'fuse/account-edit';

@Injectable({
  providedIn: 'root',
})
export class AddAccountService {
  constructor(
    private alcmeonContext: AlcmeonContextService,
    private errorHandlingService: ErrorHandlingService,
    private basicService: BasicService,
    private httpClient: HttpClient,
    private asyncRequestService: AsyncRequestService
  ) {}

  redirectToAccount(accountId: number) {
    const path = `/#/${this.alcmeonContext.companyId}/${ACCOUNT_EDIT_PATH}/${accountId}`;
    const url = this.basicService.fullURLForRoutePath(path);
    this.basicService.window.location.href = url;
  }

  getGmbLocations(params) {
    let locations = [];
    const url = `${API_URL_COMPANIES}/${this.alcmeonContext.companyId}/gmb/authorized/locations`;
    const recursiveCall = (response: any) => {
      const locationsValid = (response.locations || []).filter(location => !!location);

      if (response.next_page_token) {
        params = params.delete('next_page_token');
        params = params.append('next_page_token', response.next_page_token);
        return this.asyncRequestService.get<any>(url, params).pipe(
          tap(() => (locations = locations.concat(locationsValid))),
          tap(() => console.error(response.next_page_token, locations)),
          mergeMap(recursiveCall),
          this.errorHandlingService.catchAlertAndThrowHttpError()
        );
      } else {
        locations = locations.concat(locationsValid);
        console.error('last call', locations);
        return of(1);
      }
    };
    return this.asyncRequestService.get<any>(url, params).pipe(
      this.errorHandlingService.catchAlertAndThrowHttpError(),
      mergeMap(recursiveCall),
      map(() => ({ locations }))
    );
  }

  connect(type: string) {
    const url = `${API_URL_COMPANIES}/${this.alcmeonContext.companyId}/add-account`;
    this.httpClient
      .post<string>(url, JSON.stringify(type), { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) })
      .pipe(
        map(response => (window.location.href = response)),
        this.errorHandlingService.catchAlertAndThrowHttpError()
      )
      .subscribe();
  }

  getAuthorizedData(siteType: SiteTypeEnum, data: AuthorizedDataParams): Observable<AuthorizedData> {
    const url = `${API_URL_COMPANIES}/${this.alcmeonContext.companyId}/${siteType}/authorized/data`;
    return this.httpClient
      .post<AuthorizedData>(url, data)
      .pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }

  /**
   * The endpoint is V2, different from the one used by AJS.
   * The difference : V1 makes redirections by server, V2 returns objects and front do the redirections
   * TODO : when every component migrated in NG, tell BE to remove old endpoint 'add-account-done'.
   */
  finalizeAddAccount(siteType: SiteTypeEnum, data: { rid: number }): Observable<number> {
    const url = `${API_URL_COMPANIES_V2}/${this.alcmeonContext.companyId}/parameters/add-account-done/${siteType}`;
    return this.httpClient.post<number>(url, data).pipe(this.errorHandlingService.catchAlertAndThrowHttpError());
  }
}
